import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Table,
  TablePagination,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Tooltip,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff, CheckCircle, Cancel, PendingOutlined, AddPhotoAlternate, Close, Visibility, LocalOffer, ConfirmationNumber, Launch, Whatshot, PublishedWithChanges } from '@mui/icons-material';
import { eventsAPI, citiesAPI, districtsAPI, eventTypesAPI } from '../../services/api';
import { ApprovalStatus, ApprovalStatusLabels, APPROVAL_STATUS_FIELD } from '../../utils/enums';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageEvents, canDeleteEvents } from '../../utils/roleUtils';
import browserGlobal from '../../utils/browserGlobal';

const createEmptyEventErrors = () => ({
  title: '',
  description: '',
  cityId: '',
  districtId: '',
  eventTypeId: '',
  address: '',
  postalCode: '',
  eventOnDate: '',
  eventTime: '',
  charges: '',
  latitude: '',
  longitude: '',
  webUrl: '',
});

const EVENT_FIELD_ERROR_KEYS = new Set([
  'title',
  'description',
  'cityId',
  'districtId',
  'eventTypeId',
  'address',
  'postalCode',
  'eventOnDate',
  'eventTime',
  'charges',
]);

/** Collects banner image URLs from list/detail payloads (same sources as Promotions). */
const extractEventBannerUrls = (item) => {
  if (Array.isArray(item?.pictures)) {
    return item.pictures
      .map((picture) =>
        typeof picture === 'string'
          ? picture
          : picture?.imageUrl || picture?.url || ''
      )
      .filter(Boolean);
  }

  if (Array.isArray(item?.imageUrls)) {
    return item.imageUrls.filter(Boolean);
  }

  if (typeof item?.imageUrls === 'string') {
    return item.imageUrls.split(',').map((x) => x.trim()).filter(Boolean);
  }

  if (typeof item?.bannerImage === 'string') {
    return item.bannerImage.split(',').map((x) => x.trim()).filter(Boolean);
  }

  return [];
};

const firstEventBannerUrl = (event) => {
  const urls = extractEventBannerUrls(event);
  return urls[0] || '';
};

function getApprovalStatusChipColor(status) {
  if (status === ApprovalStatus.APPROVED) return 'success';
  if (status === ApprovalStatus.DECLINE || status === ApprovalStatus.REJECTED) return 'error';
  if (status === ApprovalStatus.INACTIVE) return 'default';
  return 'warning';
}

function getApprovalDialogButtonColor(status) {
  if (status === ApprovalStatus.APPROVED) return 'success';
  if (status === ApprovalStatus.DECLINE || status === ApprovalStatus.REJECTED) return 'error';
  return 'warning';
}

function getApprovalNoteColor(status) {
  if (status === ApprovalStatus.DECLINE || status === ApprovalStatus.REJECTED) return 'error';
  return 'textSecondary';
}

function getIsActiveFilterParam(statusFilter) {
  if (statusFilter === 'true') return true;
  if (statusFilter === 'false') return false;
  return undefined;
}

const isAddedByUser = (item) => {
  if (!item) return false;
  const type = String(
    item.addedByType ||
    item.AddedByType ||
    item.addedBy ||
    item.AddedBy ||
    ''
  ).toLowerCase().trim();
  return type === 'user' || type.includes('user');
};

function getEventFieldHelperText(errorKey, translate) {
  if (!errorKey) return '';
  const translated = translate(errorKey);
  if (translated !== errorKey) return translated;
  if (errorKey === 'events.errEventDatePast') return 'Date cannot be in the past';
  if (errorKey === 'events.errEventTimePast') return "Time must be in the future for today's events";
  if (errorKey === 'events.errAddressAutoFetch') return 'Please select address from auto-fetch suggestions';
  if (errorKey === 'events.errAddressRequired') return 'Address is required';
  return 'Required';
}


function validateURL(url) {
  if (url == null || url === '') {
    return true;
  }
  return URL.canParse(url);
}

function getCityFilterId(cityId) {
  if (cityId === 'all' || cityId === '' || cityId == null) {
    return null;
  }
  return cityId;
}

function getApprovalNoteFieldLabel(status, translate) {
  if (status === ApprovalStatus.DECLINE) return translate('promotions.declineNote');
  return translate('promotions.approvalNoteLabel');
}

function getApprovalNotePlaceholder(status, translate) {
  if (status === ApprovalStatus.DECLINE) return translate('promotions.placeholderDecline');
  return translate('promotions.placeholderOptional');
}

function getApprovalNoteHelperText(status, note, translate) {
  if (status === ApprovalStatus.DECLINE && !note.trim()) {
    return translate('promotions.declineNoteRequired');
  }
  return '';
}

function EventApprovalActions({ event, isSubmitting, onOpenApprovalDialog, translate }) {
  const rowActive = event.isActive === true || event.IsActive === true;
  if (!rowActive) return null;

  return (
    <IconButton
      color="warning"
      onClick={() => onOpenApprovalDialog(event, event.approvalStatus)}
      size="small"
      title={translate('promotions.updateStatus') || 'Change Status'}
      disabled={isSubmitting}
    >
      <PublishedWithChanges />
    </IconButton>
  );
}

EventApprovalActions.propTypes = {
  event: PropTypes.shape({
    isActive: PropTypes.bool,
    IsActive: PropTypes.bool,
    approvalStatus: PropTypes.string,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onOpenApprovalDialog: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
};

const validateUploadedFile = (file) => {
  if (!file) return null;
  const name = file.name || '';
  const lowercaseName = name.toLowerCase();

  // Allowed extensions
  const allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg'];
  const hasAllowedExt = allowedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (!hasAllowedExt) {
    return 'Only .png, .jpg, .jpeg, and .svg files are allowed.';
  }

  // Block executables/installers explicitly
  const blockedExtensions = ['.exe', '.msi', '.bat', '.sh', '.cmd', '.com', '.scr', '.vbs', '.js'];
  const hasBlockedExt = blockedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (hasBlockedExt) {
    return 'Installer and executable files (.exe, .msi, .bat, etc.) are blocked.';
  }

  // Blocks multi-extensions (abc.xyz.png)
  const dots = name.split('.').length - 1;
  if (dots > 1) {
    return 'Files with multiple extensions (e.g. abc.xyz.png) are not allowed.';
  }

  // Max size: 5 MB
  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return 'File size must be under 5 MB.';
  }

  return null;
};

const EVENT_FIELD_ORDER = [
  'title',
  'description',
  'cityId',
  'districtId',
  'address',
  'eventTypeId',
  'postalCode',
  'eventOnDate',
  'eventTime',
  'charges',
  'latitude',
  'longitude',
  'webUrl',
];

const Events = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const canAddEdit = canManageEvents(adminUser?.role);
  const canDelete = canDeleteEvents(adminUser?.role);

  const dialogContentRef = useRef(null);

  const scrollToField = (fieldKey) => {
    if (!dialogContentRef.current || !fieldKey) return;
    const container = dialogContentRef.current;
    const target =
      container.querySelector(`[data-field="${fieldKey}"]`) ||
      container.querySelector(`[name="${fieldKey}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const inputEl = target.querySelector('input, textarea, select') || (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ? target : null);
      if (inputEl) {
        setTimeout(() => {
          inputEl.focus({ preventScroll: true });
        }, 300);
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [events, setEvents] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isTrending, setIsTrending] = useState(false);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [eventToApprove, setEventToApprove] = useState(null);
  const [filters, setFilters] = useState({
    title: '',
    cityId: '',
    status: 'all'
  });

  const [debouncedTitle, setDebouncedTitle] = useState('');
  const debouncedTitleRef = useRef(debouncedTitle);
  useEffect(() => {
    debouncedTitleRef.current = debouncedTitle;
  }, [debouncedTitle]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.title || '').trim();
      const prev = debouncedTitleRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedTitle(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.title]);

  useEffect(() => {
    setPage(0);
  }, [filters.status]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cityId: '',
    eventTypeId: '',
    Pictures: [],
    existingBannerImages: [],
    picturesRemove: [],
    isEnabled: true,
    recurrence: '',
    contactPerson: '',
    personRole: '',
    contactPersonGender: '',
    minAgeAllowed: '',
    language: '',
    peopleOfEvent: [],
    whatYouGet: [],
    coupons: [],
  });

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const [newPerson, setNewPerson] = useState('');
  const [editingPersonIndex, setEditingPersonIndex] = useState(null);
  const [newWhatYouGetItem, setNewWhatYouGetItem] = useState('');
  const [editingWhatYouGetIndex, setEditingWhatYouGetIndex] = useState(null);
  const [couponInput, setCouponInput] = useState({
    title: '',
    description: '',
    code: '',
    price: '',
    url: '',
  });
  const [editingCouponIndex, setEditingCouponIndex] = useState(null);

  const handleSavePerson = (personText) => {
    const text = (typeof personText === 'string' ? personText : newPerson).trim().slice(0, 50);
    if (!text) return;
    setFormData((prev) => {
      const currentPeople = [...(prev.peopleOfEvent || [])];
      if (editingPersonIndex !== null && editingPersonIndex >= 0) {
        currentPeople[editingPersonIndex] = text;
      } else {
        currentPeople.push(text);
      }
      return { ...prev, peopleOfEvent: currentPeople };
    });
    setNewPerson('');
    setEditingPersonIndex(null);
  };

  const handleEditPerson = (index) => {
    if (formData.peopleOfEvent && formData.peopleOfEvent[index] !== undefined) {
      setNewPerson(formData.peopleOfEvent[index]);
      setEditingPersonIndex(index);
    }
  };

  const handleRemovePerson = (index) => {
    setFormData((prev) => ({
      ...prev,
      peopleOfEvent: (prev.peopleOfEvent || []).filter((_, i) => i !== index),
    }));
    if (editingPersonIndex === index) {
      setNewPerson('');
      setEditingPersonIndex(null);
    }
  };

  const handleSaveWhatYouGet = (itemText) => {
    const text = (typeof itemText === 'string' ? itemText : newWhatYouGetItem).trim();
    if (!text) return;
    setFormData((prev) => {
      const currentList = [...(prev.whatYouGet || [])];
      if (editingWhatYouGetIndex !== null && editingWhatYouGetIndex >= 0) {
        currentList[editingWhatYouGetIndex] = text;
      } else {
        currentList.push(text);
      }
      return { ...prev, whatYouGet: currentList };
    });
    setNewWhatYouGetItem('');
    setEditingWhatYouGetIndex(null);
  };

  const handleEditWhatYouGet = (index) => {
    if (formData.whatYouGet && formData.whatYouGet[index] !== undefined) {
      setNewWhatYouGetItem(formData.whatYouGet[index]);
      setEditingWhatYouGetIndex(index);
    }
  };

  const handleRemoveWhatYouGet = (index) => {
    setFormData((prev) => ({
      ...prev,
      whatYouGet: (prev.whatYouGet || []).filter((_, i) => i !== index),
    }));
    if (editingWhatYouGetIndex === index) {
      setNewWhatYouGetItem('');
      setEditingWhatYouGetIndex(null);
    }
  };

  const handleAddWhatYouGet = handleSaveWhatYouGet;

  const handleSaveCoupon = () => {
    if (!couponInput.title.trim() && !couponInput.code.trim()) {
      showToast('Coupon title or code is required', 'warning');
      return;
    }
    if (couponInput.url && !validateURL(couponInput.url)) {
      showToast('Coupon URL is invalid', 'warning');
      return;
    }

    setFormData((prev) => {
      const currentCoupons = [...(prev.coupons || [])];
      if (editingCouponIndex !== null && editingCouponIndex >= 0) {
        currentCoupons[editingCouponIndex] = { ...couponInput };
      } else {
        currentCoupons.push({ ...couponInput });
      }
      return { ...prev, coupons: currentCoupons };
    });

    setCouponInput({ title: '', description: '', code: '', price: '', url: '' });
    setEditingCouponIndex(null);
  };

  const handleEditCoupon = (index) => {
    if (formData.coupons && formData.coupons[index]) {
      setCouponInput({ ...formData.coupons[index] });
      setEditingCouponIndex(index);
    }
  };

  const handleRemoveCoupon = (index) => {
    setFormData((prev) => ({
      ...prev,
      coupons: (prev.coupons || []).filter((_, i) => i !== index),
    }));
    if (editingCouponIndex === index) {
      setCouponInput({ title: '', description: '', code: '', price: '', url: '' });
      setEditingCouponIndex(null);
    }
  };

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimerRef = useRef(null);

  const handleAddressSearch = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);

    addressSearchTimerRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const url = `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(query)}&limit=5&in=countryCode:DEU&apiKey=6Y5UiHisZ3Ig9XwBJT20IwJMXar22qUlLWDpXYVKjw0`;
        const response = await fetch(url);
        const data = await response.json();
        setAddressSuggestions(data.items || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setAddressLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);
    };
  }, []);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await districtsAPI.getDistricts(cityId, 0, 1000);
      if (response?.data?.success === true) {
        const districtsList = response.data.payload || [];
        setDistricts(districtsList);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const fetchCities = useCallback(async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success && Array.isArray(response?.data?.payload)) {
        const citiesList = response.data.payload;
        setCities(citiesList);

        // Set first city as default
        if (citiesList.length > 0) {
          const firstCity = citiesList[0];
          setFilters(prev => ({ ...prev, cityId: firstCity.id }));
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const cid = getCityFilterId(filters.cityId);
      const isActiveParam = getIsActiveFilterParam(filters.status);
      const fetchFn = isTrending ? eventsAPI.getTrendingEvents : eventsAPI.getEvents;
      const response = await fetchFn(cid, page, rowsPerPage, {
        title: debouncedTitle.trim() || undefined,
        isActive: isActiveParam,
      });
      if (response?.data?.success) {
        const rawPayload = Array.isArray(response?.data?.payload) ? response.data.payload : [];
        const transformedEvents = rawPayload.map((event) => ({
          ...event,
          ownerName: event.ownerName || event.OwnerName || '',
          approvedByName: event.approvedByName || event.ApprovedByName || event.approvedBy || event.ApprovedBy || '',
          addedByType: event.addedByType || event.AddedByType || event.addedBy || event.AddedBy || '',
        }));
        setEvents(transformedEvents);
        setTotalCount(response?.data?.totalRecord ?? transformedEvents.length);
      } else {
        setEvents([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters.cityId, filters.status, debouncedTitle, page, rowsPerPage, isTrending]);

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await eventTypesAPI.getEventTypes();
      if (response?.data?.success === true) {
        setEventTypes(Array.isArray(response.data.payload) ? response.data.payload : []);
      }
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  }, []);

  useEffect(() => {
    fetchCities();
    fetchEventTypes();
  }, [fetchCities, fetchEventTypes]);

  useEffect(() => {
    if (filters.cityId) {
      fetchEvents();
    }
  }, [filters.cityId, fetchEvents]);

  const isEventExpired = (event) => {
    if (!event) return false;
    if (event.isExpired !== undefined && event.isExpired !== null) return Boolean(event.isExpired);
    if (event.IsExpired !== undefined && event.IsExpired !== null) return Boolean(event.IsExpired);
    if (event.expired !== undefined && event.expired !== null) return Boolean(event.expired);
    if (event.IsExpiredEvent !== undefined && event.IsExpiredEvent !== null) return Boolean(event.IsExpiredEvent);

    const rawDate = event.eventOnDate ?? event.EventOnDate;
    if (!rawDate) return false;
    const yyyyMmDd = formatDateForInput(rawDate);
    if (!yyyyMmDd) return false;

    const [year, month, day] = yyyyMmDd.split('-').map(Number);
    if (!year || !month || !day) return false;

    const rawTime = event.eventTime ?? event.EventTime;
    let hours = 23;
    let minutes = 59;
    let seconds = 59;

    if (rawTime && typeof rawTime === 'string') {
      const timeMatch = rawTime.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = parseInt(timeMatch[2], 10);
        seconds = 0;
      }
    }

    const eventDateTime = new Date(year, month - 1, day, hours, minutes, seconds);
    const now = new Date();
    return eventDateTime < now;
  };

  const visibleEvents = useMemo(() => {
    const q = debouncedTitle.trim().toLowerCase();
    const hay = (v) => String(v ?? '').toLowerCase();
    return events.filter((event) => {
      if (q) {
        const matchTitle =
          hay(event.title).includes(q) ||
          hay(event.Title).includes(q) ||
          hay(event.ownerName).includes(q) ||
          hay(event.approvedByName).includes(q) ||
          hay(event.addedByType).includes(q);
        if (!matchTitle) return false;
      }
      if (filters.status === 'true' && event.isActive !== true) return false;
      if (filters.status === 'false' && event.isActive !== false) return false;
      if (filters.status === 'expired' && !isEventExpired(event)) return false;
      if (filters.status === 'upcoming' && isEventExpired(event)) return false;
      return true;
    });
  }, [events, debouncedTitle, filters.status]);

  /** HTML date input needs YYYY-MM-DD. API often returns DD-MM-YYYY or DD/MM/YYYY; `new Date('13-04-2026')` is invalid. */
  const formatDateForInput = (dateString) => {
    if (dateString == null || dateString === '') return '';
    const trimmed = String(dateString).trim();
    if (!trimmed) return '';

    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.substring(0, 10);
    }

    const dmY = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
    if (dmY) {
      const d = dmY[1].padStart(2, '0');
      const m = dmY[2].padStart(2, '0');
      const y = dmY[3];
      return `${y}-${m}-${d}`;
    }

    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  /** API expects time as HH:MM (no seconds). HTML type="time" gives HH:mm; strip seconds if present. */
  const formatTimeForAPI = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return '';
    const trimmed = timeString.trim();
    if (!trimmed) return '';
    const parts = trimmed.split(':');
    if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    return trimmed;
  };

  /** API expects date as DD-MM-YYYY. */
  const formatDateForAPI = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return '';
    const trimmed = dateString.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const [y, m, d] = trimmed.substring(0, 10).split('-');
      return `${d}-${m}-${y}`;
    }
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return trimmed;
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const formatEventDateForDisplay = (rawDate) => {
    if (rawDate == null || rawDate === '') return '-';
    const yyyyMmDd = formatDateForInput(rawDate);
    if (!yyyyMmDd) return String(rawDate);
    const d = new Date(yyyyMmDd);
    if (Number.isNaN(d.getTime())) return String(rawDate);
    return d.toLocaleDateString();
  };

  const handleOpen = async (event = null, isView = false) => {
    setViewMode(isView);
    setErrors(createEmptyEventErrors());
    setAddressSuggestions([]);
    setNewPerson('');
    setEditingPersonIndex(null);
    setNewWhatYouGetItem('');
    setEditingWhatYouGetIndex(null);
    setCouponInput({ title: '', description: '', code: '', price: '', url: '' });
    setEditingCouponIndex(null);
    if (event) {
      setIsLoading(true);
      try {
        const response = await eventsAPI.getEvent(event.id);
        const detail = (response?.data?.success === true && response.data.payload) ? response.data.payload : event;
        const detailImages = extractEventBannerUrls(detail);
        const listImages = extractEventBannerUrls(event);
        const mergedExistingImages = detailImages.length > 0 ? detailImages : listImages;
        setSelectedEvent(detail);
        const rawEventDate =
          detail.eventOnDate ??
          detail.EventOnDate ??
          event?.eventOnDate ??
          event?.EventOnDate;
        const rawEventTime = detail.eventTime ?? detail.EventTime ?? event?.eventTime ?? event?.EventTime;

        const detailPeople = detail.peopleOfEvent ?? detail.PeopleOfEvent;
        const detailWhatYouGet = detail.whatYouGet ?? detail.WhatYouGet;
        const detailCoupons = detail.coupons ?? detail.Coupons;

        const parseList = (val) => {
          if (Array.isArray(val)) return val;
          if (typeof val === 'string' && val.trim()) {
            try {
              const p = JSON.parse(val);
              if (Array.isArray(p)) return p;
            } catch (e) {
              return val.split(',').map((s) => s.trim()).filter(Boolean);
            }
          }
          return [];
        };

        const parseCoupons = (val) => {
          if (Array.isArray(val)) return val;
          if (typeof val === 'string' && val.trim()) {
            try {
              const p = JSON.parse(val);
              if (Array.isArray(p)) return p;
            } catch (e) {}
          }
          return [];
        };

        setFormData({
          title: detail.title || '',
            description: detail.description || '',
            cityId: detail.cityId || '',
            eventTypeId: detail.eventTypeId || '',
            districtId: detail.districtId || '',
            district: detail.district || '',
            address: detail.address || '',
            postalCode: detail.postalCode || '',
            houseNumber: detail.houseNumber || '',
            longitude: detail.longitude || '',
            latitude: detail.latitude || '',
            charges: detail.charges || '',
            eventOnDate: formatDateForInput(rawEventDate),
            eventTime: formatTimeForAPI(String(rawEventTime || '').trim()) || String(rawEventTime || '').trim(),
            Pictures: [],
            existingBannerImages: mergedExistingImages,
            picturesRemove: [],
            webUrl: detail.webUrl || '',
            webUrlButtonTitle: detail.webUrlButtonTitle || '',
            approvalStatus: detail.approvalStatus || 'PENDING',
            approvalNote: detail.approvalNote || '',
            isActive: detail.isActive ?? true,
            isEnabled: detail.isEnabled ?? true,
            ownerName: detail.ownerName || '',
            approvedByName: detail.approvedByName || '',
            recurrence: detail.recurrence || detail.Recurrence || '',
            contactPerson: detail.contactPerson || detail.ContactPerson || '',
            personRole: detail.personRole || detail.PersonRole || '',
            contactPersonGender: detail.contactPersonGender || detail.ContactPersonGender || '',
            minAgeAllowed: detail.minAgeAllowed ?? detail.MinAgeAllowed ?? '',
            language: detail.language || detail.Language || '',
            peopleOfEvent: parseList(detailPeople),
            whatYouGet: parseList(detailWhatYouGet),
            coupons: parseCoupons(detailCoupons)
          });

          // If there's a city, fetch its districts
          if (detail.cityId) {
            fetchDistricts(detail.cityId);
          }
          setOpen(true);
      } catch (error) {
        console.error('Error fetching event details:', error);
        showToast('Error fetching event details', 'error');
      } finally {
        setIsLoading(false);
      }
    } else {
      setSelectedEvent(null);
      setFormData({
        title: '',
        description: '',
        cityId: '',
        eventTypeId: '',
        districtId: '',
        district: '',
        address: '',
        postalCode: '',
        houseNumber: '',
        longitude: '',
        latitude: '',
        charges: '',
        eventOnDate: '',
        eventTime: '',
        Pictures: [],
        existingBannerImages: [],
        picturesRemove: [],
        webUrl: '',
        webUrlButtonTitle: '',
        approvalStatus: 'PENDING',
        approvalNote: '',
        isActive: true,
        isEnabled: true,
        ownerName: '',
        approvedByName: '',
        recurrence: '',
        contactPerson: '',
        personRole: '',
        contactPersonGender: '',
        minAgeAllowed: '',
        language: '',
        peopleOfEvent: [],
        whatYouGet: [],
        coupons: []
      });
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEvent(null);
    setViewMode(false);
    setAddressSuggestions([]);
    setErrors(createEmptyEventErrors());
    setNewPerson('');
    setEditingPersonIndex(null);
    setNewWhatYouGetItem('');
    setEditingWhatYouGetIndex(null);
    setCouponInput({ title: '', description: '', code: '', price: '', url: '' });
    setEditingCouponIndex(null);
  };

  const openApprovalDialog = (event, status) => {
    const rowActive = event?.isActive === true || event?.IsActive === true;
    if (!rowActive) return;
    setEventToApprove(event);
    setApprovalStatus(status);
    setApprovalNote('');
    setApprovalDialogOpen(true);
  };

  const closeApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setEventToApprove(null);
    setApprovalStatus('');
    setApprovalNote('');
  };

  const handleApproval = async () => {
    // Validate approval note for DECLINE status
    if (approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim()) {
      showToast(t('events.toastDeclineNote'), 'warning');
      return;
    }

    try {
      setIsSubmitting(true);
      // API accepts PENDING, APPROVED, DECLINE, INACTIVE (send as-is, do not use REJECTED)
      const response = await eventsAPI.updateEventApproval({
        id: eventToApprove.id,
        [APPROVAL_STATUS_FIELD]: approvalStatus,
        approvalNote: approvalNote.trim()
      });

      if (response?.data?.success === true) {
        closeApprovalDialog();
        showToast(t('events.toastStatusOk'), 'success');
        fetchEvents();
      } else {
        showToast(t('events.toastApprovalErrPrefix') + ': ' + (response?.data?.message || t('common.unknown')), 'error');
      }
    } catch (error) {
      console.error('Error updating approval status:', error);
      showToast(t('events.toastApprovalErrPrefix') + ': ' + (error.message || t('common.unknown')), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [errors, setErrors] = useState(() => createEmptyEventErrors());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'postalCode') {
      const v = String(value ?? '').trim();
      if (!v) {
        setErrors((prev) => ({ ...prev, postalCode: '' }));
      } else if (!/^\d{5}$/.test(v)) {
        setErrors((prev) => ({ ...prev, postalCode: 'events.errPostalFiveDigits' }));
      } else {
        setErrors((prev) => ({ ...prev, postalCode: '' }));
      }
    } else if (EVENT_FIELD_ERROR_KEYS.has(name)) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Validate available from date and time
    if (name === 'eventOnDate' || name === 'eventTime') {
      const dateVal = name === 'eventOnDate' ? value : formData.eventOnDate;
      const timeVal = name === 'eventTime' ? value : formData.eventTime;

      setErrors((prev) => {
        const nextErrors = { ...prev, eventOnDate: '', eventTime: '' };
        if (!dateVal) {
          nextErrors.eventOnDate = 'events.errEventDateRequired';
        } else {
          const [year, month, day] = dateVal.split('-').map(Number);
          const inputDateOnly = new Date(year, month - 1, day, 0, 0, 0, 0);
          const todayOnly = new Date();
          todayOnly.setHours(0, 0, 0, 0);

          if (inputDateOnly < todayOnly) {
            nextErrors.eventOnDate = 'events.errEventDatePast';
          } else if (inputDateOnly.getTime() === todayOnly.getTime()) {
            if (timeVal) {
              const [hours, minutes] = timeVal.split(':').map(Number);
              const inputDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
              const currentDateTime = new Date();
              if (inputDateTime <= currentDateTime) {
                nextErrors.eventTime = 'events.errEventTimePast';
              }
            }
          }
        }
        return nextErrors;
      });
    }

    // Validate latitude (required)
    if (name === 'latitude') {
      const latValue = parseFloat(value);
      if (value === '' || value === null || value === undefined) {
        setErrors(prev => ({ ...prev, latitude: 'events.errLatRequired' }));
      } else if (Number.isNaN(latValue)) {
        setErrors(prev => ({ ...prev, latitude: 'events.errLatNum' }));
      } else if (latValue < -90 || latValue > 90) {
        setErrors(prev => ({ ...prev, latitude: 'events.errLatRange' }));
      } else {
        setErrors(prev => ({ ...prev, latitude: '' }));
      }
    }

    // Validate longitude (required)
    if (name === 'longitude') {
      const lngValue = parseFloat(value);
      if (value === '' || value === null || value === undefined) {
        setErrors(prev => ({ ...prev, longitude: 'events.errLngRequired' }));
      } else if (Number.isNaN(lngValue)) {
        setErrors(prev => ({ ...prev, longitude: 'events.errLngNum' }));
      } else if (lngValue < -180 || lngValue > 180) {
        setErrors(prev => ({ ...prev, longitude: 'events.errLngRange' }));
      } else {
        setErrors(prev => ({ ...prev, longitude: '' }));
      }
    }

    // Validate URLs
    if (name === 'webUrl') {
      if (value && !validateURL(value)) {
        setErrors(prev => ({
          ...prev,
          webUrl: 'common.urlInvalid'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          webUrl: ''
        }));
      }
    }

    // Validate charges (required, max 6 characters, max 10000 if number)
    if (name === 'charges') {
      const v = String(value ?? '').trim();
      if (!v) {
        setErrors(prev => ({ ...prev, charges: 'events.errChargesRequired' }));
      } else if (v.length > 6) {
        setErrors(prev => ({ ...prev, charges: 'events.errChargesMaxLen' }));
      } else {
        const num = Number(v);
        if (!Number.isNaN(num) && num > 10000) {
          setErrors(prev => ({ ...prev, charges: 'events.errChargesMax' }));
        } else if (!Number.isNaN(num) && num < 0) {
          setErrors(prev => ({ ...prev, charges: 'events.errChargesMin' }));
        } else {
          setErrors(prev => ({ ...prev, charges: '' }));
        }
      }
    }
  };

  const validateEventForm = () => {
    const next = createEmptyEventErrors();
    const fd = formData;

    if (!String(fd.title || '').trim()) next.title = 'events.errTitleRequired';
    if (!String(fd.description || '').trim()) next.description = 'events.errDescriptionRequired';
    if (!fd.cityId || fd.cityId === '') next.cityId = 'events.errCityRequired';
    if (!fd.eventTypeId || fd.eventTypeId === '') next.eventTypeId = 'events.errEventTypeRequired';
    if (!fd.districtId || fd.districtId === '') next.districtId = 'events.errDistrictRequired';
    if (!String(fd.address || '').trim()) {
      next.address = 'events.errAddressRequired';
    } else if (!fd.latitude || !fd.longitude) {
      next.address = 'events.errAddressAutoFetch';
    }

    const pc = String(fd.postalCode || '').trim();
    if (!pc) next.postalCode = 'events.errPostalRequired';
    else if (!/^\d{5}$/.test(pc)) next.postalCode = 'events.errPostalFiveDigits';

    if (!String(fd.eventOnDate || '').trim()) {
      next.eventOnDate = 'events.errEventDateRequired';
    } else {
      const [year, month, day] = fd.eventOnDate.split('-').map(Number);
      const inputDateOnly = new Date(year, month - 1, day, 0, 0, 0, 0);
      const todayOnly = new Date();
      todayOnly.setHours(0, 0, 0, 0);

      if (inputDateOnly < todayOnly) {
        next.eventOnDate = 'events.errEventDatePast';
      } else if (inputDateOnly.getTime() === todayOnly.getTime() && fd.eventTime) {
        const [hours, minutes] = fd.eventTime.split(':').map(Number);
        const inputDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
        const currentDateTime = new Date();
        if (inputDateTime <= currentDateTime) {
          next.eventTime = 'events.errEventTimePast';
        }
      }
    }

    if (!String(fd.eventTime || '').trim()) {
      next.eventTime = 'events.errEventTimeRequired';
    }

    const chargesStr = String(fd.charges || '').trim();
    if (!chargesStr) {
      next.charges = 'events.errChargesRequired';
    } else if (chargesStr.length > 6) {
      next.charges = 'events.errChargesMaxLen';
    } else {
      const chargesNum = Number(chargesStr);
      if (!Number.isNaN(chargesNum) && chargesNum > 10000) {
        next.charges = 'events.errChargesMax';
      } else if (!Number.isNaN(chargesNum) && chargesNum < 0) {
        next.charges = 'events.errChargesMin';
      }
    }

    const latStr = fd.latitude;
    if (latStr === '' || latStr === null || latStr === undefined) {
      next.latitude = 'events.errLatRequired';
    } else {
      const latValue = parseFloat(latStr);
      if (Number.isNaN(latValue)) next.latitude = 'events.errLatNum';
      else if (latValue < -90 || latValue > 90) next.latitude = 'events.errLatRange';
    }

    const lngStr = fd.longitude;
    if (lngStr === '' || lngStr === null || lngStr === undefined) {
      next.longitude = 'events.errLngRequired';
    } else {
      const lngValue = parseFloat(lngStr);
      if (Number.isNaN(lngValue)) next.longitude = 'events.errLngNum';
      else if (lngValue < -180 || lngValue > 180) next.longitude = 'events.errLngRange';
    }

    if (fd.webUrl && !validateURL(fd.webUrl)) next.webUrl = 'common.urlInvalid';

    return next;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let errorMsg = '';

    for (const file of files) {
      const error = validateUploadedFile(file);
      if (error) {
        errorMsg = error;
      } else {
        validFiles.push(file);
      }
    }

    if (errorMsg) {
      showToast(errorMsg, 'warning');
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        Pictures: [...prev.Pictures, ...validFiles],
      }));
    }
    e.target.value = '';
  };

  const handleRemoveImage = (index, isExisting = false) => {
    setFormData((prev) => {
      if (isExisting) {
        const removedUrl = prev.existingBannerImages[index];
        const updatedExisting = [...prev.existingBannerImages];
        updatedExisting.splice(index, 1);
        return {
          ...prev,
          existingBannerImages: updatedExisting,
          picturesRemove: [
            ...(prev.picturesRemove || []),
            removedUrl,
          ].filter(Boolean),
        };
      }
      const updatedPictures = [...prev.Pictures];
      updatedPictures.splice(index, 1);
      return { ...prev, Pictures: updatedPictures };
    });
  };

  const handleSubmit = async () => {
    const nextErrors = validateEventForm();
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      const firstErrorField = EVENT_FIELD_ORDER.find((key) => Boolean(nextErrors[key]));
      if (firstErrorField) {
        scrollToField(firstErrorField);
      }
      return;
    }

    // API expects districtId as integer (System.Int32), not empty string
    const districtIdInt = formData.districtId === '' || formData.districtId == null
      ? 0
      : Number(formData.districtId);

    const formDataToSend = new FormData();

    const specialKeys = new Set([
      'Pictures',
      'pictures',
      'existingBannerImages',
      'picturesRemove',
      'picturesKeep',
      'peopleOfEvent',
      'whatYouGet',
      'coupons',
    ]);

    Object.keys(formData).forEach((key) => {
      if (specialKeys.has(key)) return;
      let val = formData[key];
      if (key === 'districtId') val = districtIdInt;
      else if (key === 'cityId') val = Number(formData.cityId) || 0;
      else if (key === 'eventTypeId') val = Number(formData.eventTypeId) || 0;
      else if (key === 'eventOnDate') val = formatDateForAPI(formData.eventOnDate);
      else if (key === 'eventTime') val = formatTimeForAPI(formData.eventTime);
      else if (key === 'minAgeAllowed') val = formData.minAgeAllowed !== '' && formData.minAgeAllowed !== null ? Number(formData.minAgeAllowed) : '';
      formDataToSend.append(key, val === null || val === undefined ? '' : val);
    });

    // peopleOfEvent
    const peopleList = Array.isArray(formData.peopleOfEvent) ? formData.peopleOfEvent : [];
    peopleList.forEach((person) => {
      formDataToSend.append('peopleOfEvent', person);
    });

    // whatYouGet
    const whatYouGetList = Array.isArray(formData.whatYouGet) ? formData.whatYouGet : [];
    whatYouGetList.forEach((item) => {
      formDataToSend.append('whatYouGet', item);
    });

    // coupons
    const couponsList = Array.isArray(formData.coupons) ? formData.coupons : [];
    formDataToSend.append('coupons', JSON.stringify(couponsList));
    couponsList.forEach((coupon, index) => {
      formDataToSend.append(`coupons[${index}].title`, coupon.title || '');
      formDataToSend.append(`coupons[${index}].description`, coupon.description || '');
      formDataToSend.append(`coupons[${index}].code`, coupon.code || '');
      formDataToSend.append(`coupons[${index}].price`, coupon.price || '');
      formDataToSend.append(`coupons[${index}].url`, coupon.url || '');
    });

    // Pass new image uploads as pictures (only once)
    formData.Pictures.forEach((file) => {
      formDataToSend.append('pictures', file);
    });

    // Pass kept existing image URLs as picturesKeep
    const keepUrls = (formData.existingBannerImages || [])
      .map((u) => String(u).trim())
      .filter(Boolean);
    keepUrls.forEach((url) => {
      formDataToSend.append('picturesKeep', url);
    });

    // Pass removed image URLs as picturesRemove
    const picturesRemove = formData.picturesRemove || [];
    picturesRemove.forEach((url) => {
      formDataToSend.append('picturesRemove', url);
    });

    setIsSubmitting(true);
    try {
      if (selectedEvent) {
        formDataToSend.append('id', selectedEvent.id);
        await eventsAPI.updateEvent(formDataToSend);
        showToast(t('events.toastUpdated'), 'success');
      } else {
        await eventsAPI.createEvent(formDataToSend);
        showToast(t('events.toastCreated'), 'success');
      }
      fetchEvents();
      handleClose();
    } catch (error) {
      console.error('Error saving event:', error);
      showToast(error?.response?.data?.errorMessage || t('events.toastSaveError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('events.confirmDelete'))) {
      setIsLoading(true);
      try {
        await eventsAPI.deleteEvent(id);
        showToast(t('events.toastDeleted'), 'success');
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
        showToast(error?.response?.data?.errorMessage || t('events.toastDeleteError'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClearFilters = () => {
    setPage(0);
    setIsTrending(false);
    setFilters({
      title: '',
      cityId: 'all',
      status: 'all'
    });
  };

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Typography variant="h4" color="primary">{t('events.title')}</Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
            }}
          >
            {t('events.addEvent')}
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 3, p: 2, bgcolor: '#fff', borderRadius: 1, boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)' }}>
        <Grid container spacing={2} alignItems="center">

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.city')}</InputLabel>
              <Select
                value={filters.cityId}
                label={t('common.city')}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, cityId: e.target.value }));
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('common.status')}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, status: e.target.value }));
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="true">{t('common.active')}</MenuItem>
                <MenuItem value="false">{t('common.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label={t('events.searchTitle')}
              placeholder={t('events.searchTitle')}
              value={filters.title}
              onChange={(e) => setFilters((prev) => ({ ...prev, title: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant={isTrending ? "contained" : "outlined"}
              onClick={() => {
                setIsTrending((prev) => !prev);
                setPage(0);
              }}
              fullWidth
              startIcon={<Whatshot />}
              sx={
                isTrending
                  ? {
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                      },
                    }
                  : {
                      borderColor: '#f97316',
                      color: '#ea580c',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#ea580c',
                        bgcolor: 'rgba(249, 115, 22, 0.08)',
                      },
                    }
              }
            >
              {t('events.trendingEvents')}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth
              startIcon={<FilterAltOff />}
              sx={{
                borderColor: '#0F3D3E',
                color: '#0F3D3E',
                '&:hover': {
                  borderColor: '#0a2829',
                  bgcolor: 'rgba(124, 58, 237, 0.04)'
                }
              }}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px'
            }
          }}
        >
          <Table sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.title')}</TableCell>
                <TableCell>{t('events.colEventType')}</TableCell>
                <TableCell>{t('events.colDescription')}</TableCell>
                <TableCell>{t('events.colLocation')}</TableCell>
                <TableCell>{t('events.colDateTime')}</TableCell>
                <TableCell>{t('events.colCharges')}</TableCell>
                <TableCell>{t('events.colUrls')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colOwnerName')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colAddedByType')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colApprovedByName')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleEvents.map((event) => {
                const bannerPreview = firstEventBannerUrl(event);
                const expired = isEventExpired(event);
                return (
                  <TableRow key={event.id} sx={{ '&:hover': { backgroundColor: '#ebf4f4' } }}>
                    <TableCell>
                      <Typography variant="subtitle2">{event.title}</Typography>
                    </TableCell>
                    <TableCell>
                      {event.eventTypeId ? (
                        <Chip
                          label={eventTypes.find(t => t.id === event.eventTypeId)?.title || eventTypes.find(t => t.id === event.eventTypeId)?.name || `Type ID: ${event.eventTypeId}`}
                          size="small"
                          sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', fontWeight: 500 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="body2">{event.description || '-'}</Typography>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 280 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.4
                            }}
                          >
                            {event.description || '-'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">{cities.find(c => c.id === event.cityId)?.name || t('common.unknown')}</Typography>
                        {event.district && (
                          <Typography variant="caption" color="textSecondary">
                            {event.district}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          maxWidth: 200
                        }}>
                          {event.address}
                          {event.houseNumber && `, ${event.houseNumber}`}
                          {event.postalCode && `, ${event.postalCode}`}
                        </Typography>
                        {event.longitude && event.latitude && (
                          <Typography variant="caption" color="textSecondary">
                            {event.longitude}, {event.latitude}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          {formatEventDateForDisplay(event.eventOnDate ?? event.EventOnDate)}
                        </Typography>
                        {event.eventTime && (
                          <Typography variant="caption" color="textSecondary">
                            {event.eventTime}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {event.charges
                          ? (String(event.charges).startsWith('€') || Number.isNaN(Number(event.charges))
                              ? event.charges
                              : `€${event.charges}`)
                          : t('common.free')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {bannerPreview ? (
                          <Box
                            component="img"
                            src={bannerPreview}
                            alt=""
                            referrerPolicy="no-referrer"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.style.display = 'none';
                            }}
                            sx={{ width: 60, height: 60, borderRadius: 1, objectFit: 'cover', border: '1px solid #e2efef' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              border: '1px dashed #e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f9fafb',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          </Box>
                        )}
                        {event.webUrl && (
                          <Link href={event.webUrl} target="_blank" rel="noopener" underline="hover">
                            {event.webUrlButtonTitle || t('common.visitWebsite')}
                          </Link>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {event.ownerName || event.OwnerName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(event.addedByType || event.AddedByType || event.addedBy || event.AddedBy) ? (
                        <Chip
                          label={event.addedByType || event.AddedByType || event.addedBy || event.AddedBy}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {event.approvedByName || event.ApprovedByName || event.approvedBy || event.ApprovedBy || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={ApprovalStatusLabels[event.approvalStatus] ?? event.approvalStatus}
                            color={getApprovalStatusChipColor(event.approvalStatus)}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {event.isActive && (
                            <Chip
                              label={t('common.active')}
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {event.isEnabled && (
                            <Chip
                              label={t('common.enabled')}
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {expired && (
                          <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 700, display: 'block' }}>
                            {t('events.expiredNotice')}
                          </Typography>
                        )}
                        {event.approvalNote && (
                          <Typography
                            variant="caption"
                            color={getApprovalNoteColor(event.approvalStatus)}
                            sx={{ mt: 0.5, display: 'block', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {t('common.note')}: {event.approvalNote}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {isAddedByUser(event) ? (
                          <IconButton
                            color="info"
                            onClick={() => handleOpen(event, true)}
                            size="small"
                            title={t('common.view')}
                            disabled={isSubmitting}
                          >
                            <Visibility />
                          </IconButton>
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={() => handleOpen(event, false)}
                            size="small"
                            title={t('common.edit')}
                            disabled={isSubmitting}
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(event.id)}
                            size="small"
                            title={t('common.delete')}
                            disabled={isSubmitting}
                          >
                            <Delete />
                          </IconButton>
                        )}
                        {/* Approval status actions */}
                        <EventApprovalActions
                          event={event}
                          isSubmitting={isSubmitting}
                          onOpenApprovalDialog={openApprovalDialog}
                          translate={t}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
            sx={{
              '.MuiTablePagination-select': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-selectIcon': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-displayedRows': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#0F3D3E',
                },
                '& .Mui-disabled': {
                  color: '#e9d5ff',
                }
              }
            }}
          />
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{viewMode ? t('events.dialogView') : selectedEvent ? t('events.dialogEdit') : t('events.dialogAdd')}</DialogTitle>
        <DialogContent ref={dialogContentRef} sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} data-field="title">
              <TextField
                name="title"
                label={t('events.fieldEventTitle')}
                fullWidth
                required
                margin="normal"
                value={formData.title}
                onChange={handleChange}
                error={Boolean(errors.title)}
                helperText={errors.title ? t(errors.title) : ''}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} data-field="description">
              <TextField
                name="description"
                label={t('common.description')}
                fullWidth
                required
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange}
                error={Boolean(errors.description)}
                helperText={errors.description ? t(errors.description) : ''}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-field="cityId">
              <FormControl fullWidth required error={Boolean(errors.cityId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                <InputLabel>{t('common.city')}</InputLabel>
                <Select
                  name="cityId"
                  value={formData.cityId}
                  label={`${t('common.city')} *`}
                  readOnly={viewMode}
                  onChange={(e) => {
                    const newCityId = e.target.value;
                    setErrors((prev) => ({ ...prev, cityId: '' }));
                    setFormData(prev => ({
                      ...prev,
                      cityId: newCityId,
                      districtId: '',
                      district: ''
                    }));
                    if (newCityId) {
                      fetchDistricts(newCityId);
                    } else {
                      setDistricts([]);
                    }
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                  ))}
                </Select>
                {errors.cityId ? (
                  <FormHelperText>{t(errors.cityId)}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} data-field="districtId">
              <FormControl fullWidth required disabled={!formData.cityId} error={Boolean(errors.districtId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                <InputLabel>{t('common.district')}</InputLabel>
                <Select
                  name="districtId"
                  value={formData.districtId ?? ''}
                  label={`${t('common.district')} *`}
                  readOnly={viewMode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setErrors((prev) => ({ ...prev, districtId: '' }));
                    setFormData(prev => ({
                      ...prev,
                      districtId: val === '' ? '' : Number(val),
                      district: val === '' ? '' : (districts.find(d => d.id === Number(val))?.name || '')
                    }));
                  }}
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {districts.map(district => (
                    <MenuItem key={district.id} value={district.id}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.districtId ? (
                  <FormHelperText>{t(errors.districtId)}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} data-field="address">
              <Autocomplete
                freeSolo
                loading={addressLoading}
                options={addressSuggestions}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.address?.label || ''}
                filterOptions={(x) => x}
                value={formData.address || ''}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const isObj = typeof newValue === 'object';
                    const addr = isObj ? (newValue.address || {}) : {};
                    let addressStr = '';
                    if (isObj) {
                      if (addr.street) {
                        addressStr = addr.street;
                      } else { 
                        addressStr = addr.label || '';
                      }
                    } else {
                      addressStr = String(newValue);
                    }
                    addressStr = addressStr.slice(0, 50);

                    const houseNumberStr = addr.houseNumber || '';
                    const postalCodeStr = addr.postalCode || '';

                    let lat = '';
                    let lng = '';
                    if (isObj && newValue.position) {
                      lat = newValue.position.lat;
                      lng = newValue.position.lng;
                    }
                    setFormData((prev) => ({
                      ...prev,
                      address: addressStr,
                      houseNumber: houseNumberStr || prev.houseNumber,
                      postalCode: postalCodeStr || prev.postalCode,
                      latitude: lat,
                      longitude: lng
                    }));

                    if (lat && lng) {
                      setErrors((prev) => ({
                        ...prev,
                        address: '',
                        latitude: '',
                        longitude: ''
                      }));
                    } else {
                      setErrors((prev) => ({
                        ...prev,
                        address: addressStr ? 'events.errAddressAutoFetch' : 'events.errAddressRequired',
                        latitude: 'events.errLatRequired',
                        longitude: 'events.errLngRequired'
                      }));
                    }
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      address: '',
                      houseNumber: '',
                      postalCode: '',
                      latitude: '',
                      longitude: ''
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      address: 'events.errAddressRequired',
                      latitude: 'events.errLatRequired',
                      longitude: 'events.errLngRequired'
                    }));
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  const sliced = (newInputValue || '').slice(0, 50);
                  if (reason === 'input') {
                    setFormData((prev) => ({
                      ...prev,
                      address: sliced,
                      latitude: '',
                      longitude: ''
                    }));
                  } else if (reason === 'clear') {
                    setFormData((prev) => ({
                      ...prev,
                      address: '',
                      latitude: '',
                      longitude: ''
                    }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      address: sliced
                    }));
                  }
                  handleAddressSearch(sliced);
                }}
                readOnly={viewMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('common.address') || 'Address'}
                    required
                    error={Boolean(errors.address)}
                    helperText={errors.address ? getEventFieldHelperText(errors.address, t) : ''}
                    onBlur={(e) => {
                      if (params.inputProps && typeof params.inputProps.onBlur === 'function') {
                        params.inputProps.onBlur(e);
                      }
                      const current = formDataRef.current;
                      const addrStr = String(current?.address || '').trim();
                      const latVal = current?.latitude;
                      const lngVal = current?.longitude;

                      if (!addrStr) {
                        setErrors((prev) => ({ ...prev, address: 'events.errAddressRequired' }));
                      } else if (!latVal || !lngVal) {
                        setErrors((prev) => ({ ...prev, address: 'events.errAddressAutoFetch' }));
                      } else {
                        setErrors((prev) => ({ ...prev, address: '' }));
                      }
                    }}
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 50
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addressLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-field="eventTypeId">
              <FormControl fullWidth required error={Boolean(errors.eventTypeId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                <InputLabel id="event-type-select-label">{t('events.fieldEventType')}</InputLabel>
                <Select
                  labelId="event-type-select-label"
                  name="eventTypeId"
                  value={formData.eventTypeId ?? ''}
                  label={t('events.fieldEventType')}
                  readOnly={viewMode}
                  onChange={handleChange}
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.title || type.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.eventTypeId ? (
                  <FormHelperText>
                    {t(errors.eventTypeId)}
                  </FormHelperText>
                ) : null}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="houseNumber"
                label={t('common.houseNumber')}
                fullWidth
                type="number"
                value={formData.houseNumber}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-field="postalCode">
              <TextField
                name="postalCode"
                label={t('common.postalCode')}
                fullWidth
                required
                value={formData.postalCode}
                onChange={handleChange}
                error={Boolean(errors.postalCode)}
                helperText={errors.postalCode ? t(errors.postalCode) : undefined}
                InputProps={{ readOnly: viewMode }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5 }}
              />
            </Grid>

            {/* Latitude and Longitude are automatically determined from the address autocomplete selection */}

            {/* Event Timing & Charges Row */}
            <Grid item xs={12} sm={4} data-field="eventOnDate">
              <TextField
                name="eventOnDate"
                label={t('events.fieldEventDate')}
                type="date"
                fullWidth
                required
                value={formData.eventOnDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.eventOnDate)}
                helperText={getEventFieldHelperText(errors.eventOnDate, t)}
                InputProps={{ readOnly: viewMode }}
                inputProps={{
                  min: (() => {
                    const today = new Date();
                    const yyyy = today.getFullYear();
                    const mm = String(today.getMonth() + 1).padStart(2, '0');
                    const dd = String(today.getDate()).padStart(2, '0');
                    return `${yyyy}-${mm}-${dd}`;
                  })()
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-field="eventTime">
              <TextField
                name="eventTime"
                label={t('events.fieldEventTime')}
                type="time"
                fullWidth
                required
                value={formData.eventTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.eventTime)}
                helperText={getEventFieldHelperText(errors.eventTime, t)}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-field="charges">
              <TextField
                name="charges"
                label={t('events.fieldCharges')}
                fullWidth
                required
                value={formData.charges}
                onChange={handleChange}
                error={Boolean(errors.charges)}
                helperText={errors.charges ? t(errors.charges) : ''}
                inputProps={{ maxLength: 6 }}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            {/* Event Settings & Age Row */}
            <Grid item xs={12} sm={4}>
              <TextField
                name="recurrence"
                label={t('events.fieldRecurrence')}
                fullWidth
                value={formData.recurrence}
                onChange={handleChange}
                placeholder={t('events.placeholderRecurrence')}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="language"
                label={t('events.fieldLanguage')}
                fullWidth
                value={formData.language}
                onChange={handleChange}
                placeholder={t('events.placeholderLanguage')}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="minAgeAllowed"
                label={t('events.fieldMinAge')}
                type="number"
                fullWidth
                value={formData.minAgeAllowed}
                onChange={handleChange}
                placeholder={t('events.placeholderMinAge')}
                inputProps={{ min: 0 }}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            {/* Organizer & Contact Info Row */}
            <Grid item xs={12} sm={4}>
              <TextField
                name="contactPerson"
                label={t('events.fieldContactPerson')}
                fullWidth
                value={formData.contactPerson}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="personRole"
                label={t('events.fieldPersonRole')}
                fullWidth
                value={formData.personRole}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                <InputLabel id="contact-gender-label">{t('events.fieldContactGender')}</InputLabel>
                <Select
                  labelId="contact-gender-label"
                  name="contactPersonGender"
                  value={formData.contactPersonGender || ''}
                  label={t('events.fieldContactGender')}
                  readOnly={viewMode}
                  onChange={handleChange}
                >
                  <MenuItem value=""><em>{t('events.genderNone')}</em></MenuItem>
                  <MenuItem value="Female">{t('events.genderFemale')}</MenuItem>
                  <MenuItem value="Male">{t('events.genderMale')}</MenuItem>
                  <MenuItem value="Other">{t('events.genderOther')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* People of Event */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                  {t('events.secPeopleOfEvent')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: (formData.peopleOfEvent || []).length ? 2 : 0 }}>
                  {(formData.peopleOfEvent || []).map((person, idx) => {
                    const isEditingThis = editingPersonIndex === idx;
                    return (
                      <Chip
                        key={idx}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 300 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {person}
                            </Typography>
                            {!viewMode && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPerson(idx);
                                }}
                                sx={{
                                  p: 0.25,
                                  color: isEditingThis ? '#15803d' : '#0F3D3E',
                                  '&:hover': { color: '#0F3D3E', bgcolor: 'rgba(15, 61, 62, 0.12)' },
                                }}
                                title={t('common.edit') || 'Edit'}
                              >
                                <Edit sx={{ fontSize: 13 }} />
                              </IconButton>
                            )}
                          </Box>
                        }
                        onDelete={viewMode ? undefined : () => handleRemovePerson(idx)}
                        onClick={viewMode ? undefined : () => handleEditPerson(idx)}
                        sx={{
                          bgcolor: isEditingThis ? '#dcfce7' : '#ebf4f4',
                          color: '#0F3D3E',
                          fontWeight: 600,
                          border: '1.5px solid',
                          borderColor: isEditingThis ? '#22c55e' : '#b2d8d8',
                          cursor: viewMode ? 'default' : 'pointer',
                          '& .MuiChip-deleteIcon': {
                            color: '#0F3D3E',
                            '&:hover': { color: '#ef4444' },
                          },
                        }}
                      />
                    );
                  })}
                  {!(formData.peopleOfEvent || []).length && (
                    <Typography variant="caption" color="text.secondary">
                      {t('events.emptyPeopleOfEvent')}
                    </Typography>
                  )}
                </Box>
                {!viewMode && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t('events.placeholderAddPerson')}
                      value={newPerson}
                      onChange={(e) => setNewPerson(e.target.value.slice(0, 50))}
                      inputProps={{ maxLength: 50 }}
                      helperText={`${newPerson.length}/50`}
                      FormHelperTextProps={{ sx: { textAlign: 'right', m: 0, mt: 0.5, fontSize: '0.7rem' } }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSavePerson();
                        }
                      }}
                    />
                    {editingPersonIndex !== null && (
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setNewPerson('');
                          setEditingPersonIndex(null);
                        }}
                        sx={{ whiteSpace: 'nowrap', color: '#64748b', borderColor: '#cbd5e1', height: 40 }}
                      >
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      startIcon={editingPersonIndex !== null ? <Edit /> : <Add />}
                      onClick={() => handleSavePerson()}
                      sx={{ whiteSpace: 'nowrap', bgcolor: '#0F3D3E', '&:hover': { bgcolor: '#0a2829' }, height: 40 }}
                    >
                      {editingPersonIndex !== null ? (t('common.update') || 'Update') : t('events.btnAddPerson')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* What You Get */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                  {t('events.secWhatYouGet')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: (formData.whatYouGet || []).length ? 2 : 0 }}>
                  {(formData.whatYouGet || []).map((item, idx) => {
                    const isEditingThis = editingWhatYouGetIndex === idx;
                    return (
                      <Chip
                        key={idx}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 300 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item}
                            </Typography>
                            {!viewMode && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditWhatYouGet(idx);
                                }}
                                sx={{
                                  p: 0.25,
                                  color: isEditingThis ? '#7e22ce' : '#6b21a8',
                                  '&:hover': { color: '#581c87', bgcolor: 'rgba(107, 33, 168, 0.12)' },
                                }}
                                title={t('common.edit') || 'Edit'}
                              >
                                <Edit sx={{ fontSize: 13 }} />
                              </IconButton>
                            )}
                          </Box>
                        }
                        onDelete={viewMode ? undefined : () => handleRemoveWhatYouGet(idx)}
                        onClick={viewMode ? undefined : () => handleEditWhatYouGet(idx)}
                        sx={{
                          bgcolor: isEditingThis ? '#f3e8ff' : '#faf5ff',
                          color: '#6b21a8',
                          fontWeight: 600,
                          border: '1.5px solid',
                          borderColor: isEditingThis ? '#a855f7' : '#e9d5ff',
                          cursor: viewMode ? 'default' : 'pointer',
                          '& .MuiChip-deleteIcon': {
                            color: '#6b21a8',
                            '&:hover': { color: '#ef4444' },
                          },
                        }}
                      />
                    );
                  })}
                  {!(formData.whatYouGet || []).length && (
                    <Typography variant="caption" color="text.secondary">
                      {t('events.emptyWhatYouGet')}
                    </Typography>
                  )}
                </Box>
                {!viewMode && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t('events.placeholderAddWhatYouGet')}
                      value={newWhatYouGetItem}
                      onChange={(e) => setNewWhatYouGetItem(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSaveWhatYouGet();
                        }
                      }}
                    />
                    {editingWhatYouGetIndex !== null && (
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setNewWhatYouGetItem('');
                          setEditingWhatYouGetIndex(null);
                        }}
                        sx={{ whiteSpace: 'nowrap', color: '#64748b', borderColor: '#cbd5e1', height: 40 }}
                      >
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      startIcon={editingWhatYouGetIndex !== null ? <Edit /> : <Add />}
                      onClick={() => handleSaveWhatYouGet()}
                      sx={{ whiteSpace: 'nowrap', bgcolor: '#0F3D3E', '&:hover': { bgcolor: '#0a2829' }, height: 40 }}
                    >
                      {editingWhatYouGetIndex !== null ? (t('common.update') || 'Update') : t('events.btnAddWhatYouGet')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Coupons Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.5,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #fafafa 0%, #f4f6f8 100%)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalOffer sx={{ color: '#0F3D3E', fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F3D3E', fontSize: '0.95rem' }}>
                      {t('events.secCoupons')}
                    </Typography>
                    {(formData.coupons || []).length > 0 && (
                      <Chip
                        label={`${formData.coupons.length} ${formData.coupons.length === 1 ? t('events.offer') : t('events.offers')}`}
                        size="small"
                        sx={{
                          bgcolor: '#ebf4f4',
                          color: '#0F3D3E',
                          fontWeight: 700,
                          fontSize: '0.725rem',
                          height: 22,
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Coupons List Cards */}
                <Grid container spacing={2} sx={{ mb: (formData.coupons || []).length ? 2.5 : 1 }}>
                  {(formData.coupons || []).map((coupon, idx) => {
                    const isEditing = editingCouponIndex === idx;
                    return (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2.5,
                            bgcolor: isEditing ? '#f0fdf4' : '#ffffff',
                            border: '1.5px solid',
                            borderColor: isEditing ? '#0F3D3E' : '#e2e8f0',
                            boxShadow: isEditing
                              ? '0 0 0 3px rgba(15, 61, 62, 0.12)'
                              : '0 2px 6px rgba(0, 0, 0, 0.04)',
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              borderColor: isEditing ? '#0F3D3E' : '#cbd5e1',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              bgcolor: isEditing ? '#0F3D3E' : '#2563eb',
                            }}
                          />

                          <Box sx={{ pl: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', pr: 1 }}>
                                {coupon.title || t('events.untitledOffer')}
                              </Typography>
                              {!viewMode && (
                                <Box sx={{ display: 'flex', gap: 0.5, shrink: 0 }}>
                                  <Tooltip title={t('events.tooltipEditCoupon')}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditCoupon(idx)}
                                      sx={{
                                        color: '#2563eb',
                                        bgcolor: '#eff6ff',
                                        p: 0.5,
                                        '&:hover': { bgcolor: '#dbeafe' },
                                      }}
                                    >
                                      <Edit sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('events.tooltipDeleteCoupon')}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveCoupon(idx)}
                                      sx={{
                                        color: '#ef4444',
                                        bgcolor: '#fef2f2',
                                        p: 0.5,
                                        '&:hover': { bgcolor: '#fee2e2' },
                                      }}
                                    >
                                      <Delete sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                              {coupon.code && (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    bgcolor: '#f1f5f9',
                                    border: '1px dashed #94a3b8',
                                    borderRadius: 1.5,
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    color: '#0f172a',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  <ConfirmationNumber sx={{ fontSize: 14, color: '#64748b' }} />
                                  {coupon.code}
                                </Box>
                              )}
                              {coupon.price && (
                                <Chip
                                  label={coupon.price}
                                  size="small"
                                  sx={{
                                    bgcolor: '#dcfce7',
                                    color: '#15803d',
                                    fontWeight: 700,
                                    fontSize: '0.725rem',
                                    height: 22,
                                    border: '1px solid #bbf7d0',
                                  }}
                                />
                              )}
                            </Box>

                            {coupon.description && (
                              <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.825rem', mb: 1, lineHeight: 1.4 }}>
                                {coupon.description}
                              </Typography>
                            )}

                            {coupon.url && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Launch sx={{ fontSize: 13, color: '#0F3D3E' }} />
                                <Link
                                  href={coupon.url}
                                  target="_blank"
                                  rel="noopener"
                                  underline="hover"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: '#0F3D3E',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 240,
                                  }}
                                >
                                  {coupon.url}
                                </Link>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                  {!(formData.coupons || []).length && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2.5,
                          textAlign: 'center',
                          border: '1px dashed #cbd5e1',
                          borderRadius: 2,
                          bgcolor: '#ffffff',
                        }}
                      >
                        <ConfirmationNumber sx={{ fontSize: 32, color: '#94a3b8', mb: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          {t('events.emptyCouponsTitle')}
                        </Typography>
                        {!viewMode && (
                          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.25 }}>
                            {t('events.emptyCouponsSub')}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Add/Edit Coupon Form */}
                {!viewMode && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      bgcolor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                      mt: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E', fontSize: '0.875rem' }}>
                        {editingCouponIndex !== null ? t('events.titleEditCoupon') : t('events.titleAddCoupon')}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label={t('events.lblCouponTitle')}
                          placeholder={t('events.placeholderCouponTitle')}
                          value={couponInput.title}
                          onChange={(e) => setCouponInput((prev) => ({ ...prev, title: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          size="small"
                          fullWidth
                          label={t('events.lblCouponCode')}
                          placeholder={t('events.placeholderCouponCode')}
                          value={couponInput.code}
                          onChange={(e) => setCouponInput((prev) => ({ ...prev, code: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          size="small"
                          fullWidth
                          label={t('events.lblCouponPrice')}
                          placeholder={t('events.placeholderCouponPrice')}
                          value={couponInput.price}
                          onChange={(e) => setCouponInput((prev) => ({ ...prev, price: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label={t('events.lblCouponUrl')}
                          placeholder={t('events.placeholderCouponUrl')}
                          value={couponInput.url}
                          onChange={(e) => setCouponInput((prev) => ({ ...prev, url: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label={t('common.description')}
                          placeholder={t('events.placeholderCouponDesc')}
                          value={couponInput.description}
                          onChange={(e) => setCouponInput((prev) => ({ ...prev, description: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 0.5 }}>
                        {editingCouponIndex !== null && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setCouponInput({ title: '', description: '', code: '', price: '', url: '' });
                              setEditingCouponIndex(null);
                            }}
                            sx={{ color: '#64748b', borderColor: '#cbd5e1' }}
                          >
                            {t('common.cancel')}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add />}
                          onClick={handleSaveCoupon}
                          sx={{
                            bgcolor: '#0F3D3E',
                            color: '#ffffff',
                            fontWeight: 600,
                            px: 2.5,
                            '&:hover': { bgcolor: '#0a2829' },
                          }}
                        >
                          {editingCouponIndex !== null ? t('events.btnUpdateCoupon') : t('events.btnAddCoupon')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </Box>
            </Grid>



            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                {t('events.secBannerImage')}
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  bgcolor: '#f9fafb',
                  minHeight: '100px',
                  mb: 2,
                }}
              >
                <Grid container spacing={2}>
                  {formData.existingBannerImages.map((url, idx) => (
                    <Grid item key={`existing-${idx}`}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 },
                        }}
                      >
                        <img
                          src={url}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                        {!viewMode && (
                          <IconButton
                            size="small"
                            className="delete-btn"
                            onClick={() => handleRemoveImage(idx, true)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: '#fff',
                              opacity: 0.8,
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              p: 0.5,
                            }}
                          >
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  ))}
                  {formData.Pictures.map((file, idx) => (
                    <Grid item key={`new-${idx}`}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 },
                        }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                        {!viewMode && (
                          <IconButton
                            size="small"
                            className="delete-btn"
                            onClick={() => handleRemoveImage(idx)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: '#fff',
                              opacity: 0.8,
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              p: 0.5,
                            }}
                          >
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  ))}
                  {!viewMode && (
                    <Grid item>
                      <Button
                        component="label"
                        sx={{
                          width: 80,
                          height: 80,
                          border: '2px dashed #d1d5db',
                          borderRadius: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6b7280',
                          textTransform: 'none',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#0F3D3E',
                            color: '#0F3D3E',
                            bgcolor: 'rgba(124, 58, 237, 0.04)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <AddPhotoAlternate sx={{ fontSize: 28 }} />
                        <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>
                          {t('common.add')}
                        </Typography>
                        <input type="file" hidden multiple accept="image/*" onChange={handleFileChange} />
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="webUrl"
                label={t('promotions.fieldWebUrl')}
                fullWidth
                value={formData.webUrl}
                onChange={handleChange}
                error={!!errors.webUrl}
                helperText={errors.webUrl ? t(errors.webUrl) : ''}
                placeholder="https://example.com"
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="webUrlButtonTitle"
                label={t('events.fieldWebUrlBtn')}
                fullWidth
                value={formData.webUrlButtonTitle}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>

            {canAddEdit && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isActive',
                            value: e.target.checked
                          }
                        })}
                        color="primary"
                        sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}
                      />
                    }
                    label={t('common.active')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isEnabled}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isEnabled',
                            value: e.target.checked
                          }
                        })}
                        color="primary"
                        sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}
                      />
                    }
                    label={t('common.enabled')}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>{viewMode ? t('common.close') || 'Close' : t('common.cancel')}</Button>
          {!viewMode && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
            >
              {selectedEvent ? t('common.update') : t('common.create')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={closeApprovalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('events.approvalDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={approvalStatus}
                label={t('common.status')}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                <MenuItem value={ApprovalStatus.APPROVED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.APPROVED]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.DECLINE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="error" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.DECLINE]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.PENDING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PendingOutlined color="warning" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.PENDING]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.INACTIVE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="disabled" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.INACTIVE]}</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={getApprovalNoteFieldLabel(approvalStatus, t)}
              name="approvalNote"
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              multiline
              rows={4}
              required={approvalStatus === ApprovalStatus.DECLINE}
              error={approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim()}
              helperText={getApprovalNoteHelperText(approvalStatus, approvalNote, t)}
              placeholder={getApprovalNotePlaceholder(approvalStatus, t)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color={getApprovalDialogButtonColor(approvalStatus)}
            disabled={isSubmitting || (approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim())}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? t('common.updating') : t('promotions.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Events;
